import React from 'react';
import {
    Autocomplete,
    CircularProgress,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {searchRequirementsList} from '../../../firebase/firestore/icr';
import UserAvatar from '../../../components/UserAvatar';

const EMPTY_EXCLUDE_IDS = [];

function mapIcrToOption(item) {
    const number = item?.number || item?.icrNumber || '';
    const title = item?.title || item?.question || number || item?.id || '';
    const authorUserId = item?.authorUserId || null;
    const profile = item?.authorProfile || null;
    const authorDisplayName = item?.authorDisplayName || item?.author || null;
    const hasAvatar = profile?.hasAvatar === true;
    return {
        id: item.id,
        title,
        icrNumber: number,
        authorUserId,
        authorDisplayName,
        hasAvatar,
    };
}

export default function ChartIcrPicker({
    active = false,
    excludeIds = EMPTY_EXCLUDE_IDS,
    onSelect,
    onCancel,
    saving = false,
}) {
    const [options, setOptions] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [loadError, setLoadError] = React.useState('');
    const [query, setQuery] = React.useState('');

    const excluded = React.useMemo(() => new Set(excludeIds), [excludeIds]);

    React.useEffect(() => {
        if (!active) {
            setLoadError('');
            setQuery('');
            setOptions([]);
            return undefined;
        }

        let cancelled = false;
        const timer = setTimeout(async () => {
            setLoading(true);
            setLoadError('');
            try {
                const data = await searchRequirementsList({
                    entityType: 'icr',
                    q: query,
                    page: 0,
                    size: 25,
                });
                if (cancelled) return;
                const items = (Array.isArray(data?.items) ? data.items : [])
                    .filter((item) => item?.id && !excluded.has(item.id))
                    .map(mapIcrToOption);
                setOptions(items);
            } catch (err) {
                if (!cancelled) {
                    setLoadError(err?.message || 'Failed to search ICRs.');
                    setOptions([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, query ? 300 : 0);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [active, query, excluded]);

    const handleChange = (_event, value) => {
        if (saving || !value) return;
        onSelect?.(value);
    };

    const handleInputChange = (_event, value, reason) => {
        if (reason === 'input') {
            setQuery(value);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape' && !saving) {
            event.preventDefault();
            event.stopPropagation();
            onCancel?.();
        }
    };

    if (!active) return null;

    return (
        <Autocomplete
            openOnFocus
            autoHighlight
            disablePortal
            size="small"
            fullWidth
            options={options}
            filterOptions={(opts) => opts}
            getOptionLabel={(option) => option.title || option.icrNumber || option.id || ''}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            loading={loading}
            disabled={saving}
            onChange={handleChange}
            onInputChange={handleInputChange}
            noOptionsText={loadError || (query ? 'No ICRs found.' : 'Type to search ICRs…')}
            renderInput={(params) => (
                <TextField
                    {...params}
                    autoFocus
                    label="ICR"
                    placeholder="Search ICRs…"
                    error={Boolean(loadError)}
                    helperText={loadError || undefined}
                    onKeyDown={handleKeyDown}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading || saving ? (
                                    <CircularProgress color="inherit" size={14}/>
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            renderOption={(props, option) => {
                const {key, ...liProps} = props;
                const ownerUser = option.authorUserId
                    ? {userId: option.authorUserId, displayName: option.authorDisplayName, hasAvatar: option.hasAvatar}
                    : null;
                return (
                    <li key={key} {...liProps} style={{...liProps.style, paddingTop: 6, paddingBottom: 6}}>
                        <Stack spacing={0.25} sx={{width: '100%', minWidth: 0}}>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                <Typography variant="body2" sx={{flex: 1, minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word'}}>
                                    {option.title}
                                </Typography>
                                {option.icrNumber ? (
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{flexShrink: 0}}>
                                        {option.icrNumber}
                                    </Typography>
                                ) : null}
                            </Stack>
                            {ownerUser && (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <UserAvatar user={ownerUser} userId={option.authorUserId} sx={{width: 14, height: 14, fontSize: 8}}/>
                                    <Typography variant="caption" color="text.secondary">
                                        {option.authorDisplayName || option.authorUserId}
                                    </Typography>
                                </Stack>
                            )}
                        </Stack>
                    </li>
                );
            }}
            slotProps={{
                popper: {
                    placement: 'bottom-start',
                    sx: {zIndex: (theme) => theme.zIndex.modal + 1},
                },
            }}
        />
    );
}
